/**
 * 計助ダッシュボード: 縦断データ（群別時系列）
 */

import { useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import KeisukeChart from '../charts/KeisukeChart';
import KeisukeAxis from '../charts/KeisukeAxis';
import { createLinearScale, calculateNiceTicks, getColor } from '../charts/chartUtils';
import type { AxisScale, ChartBounds, ChartDimensions } from '../charts/types';
import type { ResultCache } from '../../../../stores/systems/keisuke/dashboardStore';
import styles from './KeisukeDashboardChart.module.scss';
import { DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX } from './keisukeDashboardBarChartSettings';
import { KeisukeDashboardChartExportDropdown } from './KeisukeDashboardChartExportDropdown';
import { useKeisukeDashboardChartSvgExport } from './keisukeDashboardChartSvgExport';

const MARGIN = { top: 40, right: 40, bottom: 60, left: 80 };

export type KeisukeDashboardLongitudinalChartLayout = 'split-settings' | 'split-plot';

type LongitudinalChartData = NonNullable<ResultCache['longitudinalChart']>;

interface Props {
  chart: LongitudinalChartData;
  timeLabel?: string;
  valueLabel?: string;
  isCalculating?: boolean;
  layout: KeisukeDashboardLongitudinalChartLayout;
}

export default function KeisukeDashboardLongitudinalChart({
  chart,
  timeLabel = '',
  valueLabel = '',
  isCalculating = false,
  layout,
}: Props) {
  const { t } = useTranslation();
  const chartContainerRef = useRef<HTMLDivElement>(null);
  const exportHandlers = useKeisukeDashboardChartSvgExport(chartContainerRef, 'keisuke_longitudinal');

  const xDomain = useMemo((): [number, number] => {
    const times = new Set<number>();
    chart.series.forEach((s) => s.points.forEach((p) => times.add(p.time)));
    const arr = Array.from(times).sort((a, b) => a - b);
    if (arr.length === 0) return [0, 1];
    const min = arr[0];
    const max = arr[arr.length - 1];
    const pad = (max - min) * 0.05 || 1;
    return [min - pad, max + pad];
  }, [chart]);

  const yDomain = useMemo((): [number, number] => {
    let min = Infinity;
    let max = -Infinity;
    chart.series.forEach((s) =>
      s.points.forEach((p) => {
        if (p.mean < min) min = p.mean;
        if (p.mean > max) max = p.mean;
      })
    );
    if (!Number.isFinite(min)) return [0, 1];
    const pad = (max - min) * 0.1 || 1;
    return [min - pad, max + pad];
  }, [chart]);

  const xTicks = useMemo(() => calculateNiceTicks(xDomain, 5), [xDomain]);
  const yTicks = useMemo(() => calculateNiceTicks(yDomain, 5), [yDomain]);

  const plotAreaBounds: ChartBounds = useMemo(
    () => ({
      x: MARGIN.left,
      y: MARGIN.top,
      width: 640 - MARGIN.left - MARGIN.right,
      height: DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX - MARGIN.top - MARGIN.bottom,
    }),
    []
  );

  const xScale = useMemo(
    () => createLinearScale(xDomain, [0, plotAreaBounds.width]),
    [xDomain, plotAreaBounds.width]
  );
  const yScale = useMemo(
    () => createLinearScale(yDomain, [0, plotAreaBounds.height]),
    [yDomain, plotAreaBounds.height]
  );
  const yScaleRev = (v: number) => plotAreaBounds.height - yScale(v);

  if (layout === 'split-plot' && chart.series.length === 0) {
    return (
      <div className={`${styles.chartRootWrapper} ${styles.chartPlaceholder}`}>
        <span className="text-muted small">
          {t('keisuke.workspace.chartRunAnalysis')}
        </span>
      </div>
    );
  }

  return (
    <div
      ref={chartContainerRef}
      className={`${styles.chartRootWrapper} ${isCalculating ? styles.chartRootCalculating : ''}`}
    >
      {layout === 'split-plot' && (
        <KeisukeDashboardChartExportDropdown
          onCopyClipboard={() => void exportHandlers.handleCopySvgToClipboard()}
          onExportPng={exportHandlers.handleExportPng}
          onExportSvg={exportHandlers.handleExportSvg}
          onExportTiff={exportHandlers.handleExportTiff}
          onExportJpeg={exportHandlers.handleExportJpeg}
        />
      )}
      <KeisukeChart width={layout === 'split-plot' ? undefined : 640} height={DEFAULT_DASHBOARD_CHART_HEIGHT_WHEN_AUTO_PX}>
        {(dimensions: ChartDimensions) => {
          const xAxisScale: AxisScale = {
            domain: xDomain,
            range: [0, plotAreaBounds.width],
            ticks: xTicks,
          };
          const yAxisScale: AxisScale = {
            domain: yDomain,
            range: [0, plotAreaBounds.height],
            ticks: yTicks,
          };
          return (
            <>
              <KeisukeAxis
                bounds={plotAreaBounds}
                dimensions={dimensions}
                type="x"
                scale={xAxisScale}
                label={timeLabel || t('keisuke.longitudinal.time')}
              />
              <KeisukeAxis
                bounds={plotAreaBounds}
                dimensions={dimensions}
                type="y"
                scale={yAxisScale}
                label={valueLabel || t('keisuke.longitudinal.value')}
                showGrid
              />
              <g transform={`translate(${plotAreaBounds.x}, ${plotAreaBounds.y})`} data-keisuke-chart-graph>
                {chart.series.map((series, groupIndex) => {
                  const sorted = [...series.points].sort((a, b) => a.time - b.time);
                  if (sorted.length === 0) return null;
                  const color = getColor(groupIndex);
                  const pathData = sorted
                    .map((p, i) => `${i === 0 ? 'M' : 'L'} ${xScale(p.time)},${yScaleRev(p.mean)}`)
                    .join(' ');
                  return (
                    <g key={series.group}>
                      <path d={pathData} fill="none" stroke={color} strokeWidth={2} />
                      {sorted.map((p, i) => (
                        <circle
                          key={i}
                          cx={xScale(p.time)}
                          cy={yScaleRev(p.mean)}
                          r={4}
                          fill={color}
                        />
                      ))}
                    </g>
                  );
                })}
              </g>
            </>
          );
        }}
      </KeisukeChart>
    </div>
  );
}
